import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

export type Role = "owner" | "editor" | "viewer" | "admin";

type State = {
  role: Role | null;
  isAdmin: boolean;
  loading: boolean;
  error: Error | null;
};

/**
 * Resolve the current user's role for a given survey.
 * Fetches both the user account role (/users/{uid}) and the survey doc (/surveys/{surveyId}).
 */
export function useSurveyRole(surveyId?: string) {
  const [state, setState] = useState<State>({
    role: null,
    isAdmin: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const user = auth.currentUser;
      if (!user || !surveyId) {
        if (!cancelled) {
          setState({ role: null, isAdmin: false, loading: false, error: null });
        }
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const accountRole = userSnap.exists() ? (userSnap.data() as any)?.accountRole : undefined;
        const isAdmin = accountRole === "admin";

        const surveySnap = await getDoc(doc(db, "surveys", surveyId));
        if (!surveySnap.exists()) {
          if (!cancelled) {
            setState({ role: null, isAdmin, loading: false, error: null });
          }
          return;
        }

        const data = surveySnap.data() as any;
        const uid = user.uid;

        let role: Role | null = null;
        if (data.userId === uid) {
          role = "owner";
        } else if (isAdmin) {
          role = "admin";
        } else {
          const explicitRole = data.roles?.[uid];
          if (explicitRole === "editor" || explicitRole === "viewer" || explicitRole === "owner") {
            role = explicitRole;
          } else if (Array.isArray(data.memberIds) && data.memberIds.includes(uid)) {
            role = "viewer";
          }
        }

        if (!cancelled) {
          setState({ role, isAdmin, loading: false, error: null });
        }
      } catch (err: any) {
        if (!cancelled) {
          setState({ role: null, isAdmin: false, loading: false, error: err });
        }
      }
    }

    setState((prev) => ({ ...prev, loading: true }));
    load();

    return () => {
      cancelled = true;
    };
  }, [surveyId]);

  return state;
}
